import React from "react";
import { motion } from "framer-motion";
import { ChevronRight, Check } from "lucide-react";
import mockup1 from "../../assets/store/mockup-center.png";
import mockup2 from "../../assets/store/mockup-left.png";
import mockup3 from "../../assets/store/mockup-right.png";
import noise from "../../assets/store/noise.png";
import { useTranslation } from "react-i18next";

export default function Hero() {
	const { t } = useTranslation();
	const primary = "#43BD98";
	const accent = "#F2C94C";
	const quickBenefits = t("store.hero.quickBenefits", { returnObjects: true });

	return (
		<section
			id="inicio"
			className="relative overflow-hidden bg-gradient-to-b from-white via-[#f9fcfb] to-[#f8f8f8] text-gray-900"
			style={{ fontFamily: "'Poppins', sans-serif" }}
		>
			{/* Fondos decorativos */}
			<div
				className="absolute w-[600px] h-[600px] bg-[#E9FAF3] rounded-full blur-[120px] opacity-50 top-[-200px] left-[-200px]"
				aria-hidden="true"
			/>
			<div
				className="absolute w-[400px] h-[400px] bg-[#FFF2CC] rounded-full blur-[100px] opacity-40 bottom-[-150px] right-[-100px]"
				aria-hidden="true"
			/>
			<div
				className="absolute inset-0 opacity-[0.8] pointer-events-none"
				style={{
					backgroundImage: `url(${noise})`,
					backgroundRepeat: "repeat",
					backgroundSize: "250px 250px",
				}}
			/>

			{/* Contenido principal */}
			<div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-16 pt-36 md:pt-48 pb-24 grid grid-cols-1 md:grid-cols-2 gap-20 items-center">
				{/* Texto */}
				<div className="text-center md:text-left">
					{/* Trial Badge */}
					<motion.div
						initial={{ opacity: 0, y: -10 }}
						animate={{ opacity: 1, y: 0 }}
						transition={{ duration: 0.5 }}
						className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium mb-6"
						style={{
							backgroundColor: `${primary}15`,
							color: primary,
							border: `1px solid ${primary}30`,
						}}
					>
						{t("store.hero.trialBadge")}
					</motion.div>

					<motion.h1
						className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6"
						style={{ lineHeight: "1.1" }}
						initial={{ opacity: 0, y: -20 }}
						animate={{ opacity: 1, y: 0 }}
						transition={{ duration: 0.6, ease: "easeOut" }}
					>
						{t("store.hero.title.part1")}{" "}
						<span style={{ color: primary }}>
							{t("store.hero.title.highlight")}
						</span>
					</motion.h1>

					<motion.p
						className="text-gray-600 text-base md:text-lg max-w-lg mx-auto lg:mx-0 leading-relaxed mb-6"
						initial={{ opacity: 0, y: 10 }}
						animate={{ opacity: 1, y: 0 }}
						transition={{ duration: 0.7, ease: "easeOut", delay: 0.15 }}
					>
						{t("store.hero.subtitle")}
					</motion.p>

					{/* Quick Benefits */}
					<motion.div
						className="flex flex-wrap justify-center md:justify-start gap-x-5 gap-y-2 mb-8"
						initial={{ opacity: 0, y: 10 }}
						animate={{ opacity: 1, y: 0 }}
						transition={{ duration: 0.7, ease: "easeOut", delay: 0.2 }}
					>
						{quickBenefits.map((benefit, index) => (
							<div
								key={index}
								className="flex items-center gap-2 text-sm text-gray-700"
							>
								<Check
									size={16}
									className="flex-shrink-0"
									style={{ color: primary }}
								/>
								<span>{benefit}</span>
							</div>
						))}
					</motion.div>

					<motion.div
						className="flex justify-center md:justify-start gap-4 flex-wrap"
						initial={{ opacity: 0, y: 10 }}
						animate={{ opacity: 1, y: 0 }}
						transition={{ duration: 0.8, ease: "easeOut", delay: 0.3 }}
					>
						<a
							href="https://creator.applaby.com?onboarding=true&appType=Ecommerce&appSubtype=Ecommerce"
							target="_blank"
							rel="noreferrer"
							className="px-8 py-3 rounded-xl font-semibold shadow-md hover:shadow-lg hover:scale-105 transition flex items-center gap-2"
							style={{
								background: `linear-gradient(135deg, ${primary} 0%, #2fae83 100%)`,
								color: "#fff",
							}}
						>
							{t("store.hero.ctaPrimary")} <ChevronRight size={18} />
						</a>

						<a
							href="#demo"
							className="border border-gray-300 text-gray-800 px-8 py-3 rounded-xl font-semibold hover:bg-gray-100 transition flex items-center gap-2"
						>
							{t("store.hero.ctaSecondary")}
						</a>
					</motion.div>
				</div>

				{/* Mockups */}
				<div className="relative w-full flex justify-center">
					{/* Móvil */}
					<div className="flex md:hidden items-end justify-center relative h-[320px]">
						<motion.img
							src={mockup2}
							alt="App izquierda"
							className="absolute left-1/2 bottom-0 w-48 h-auto drop-shadow-2xl -translate-x-[70%] z-10"
							initial={{ opacity: 0, y: 30 }}
							animate={{ opacity: 1, y: 0 }}
							transition={{ duration: 0.6 }}
						/>
						<motion.img
							src={mockup1}
							alt="App central"
							className="w-56 h-auto drop-shadow-2xl relative z-20"
							initial={{ opacity: 0, y: 30 }}
							animate={{ opacity: 1, y: 20 }}
							transition={{ duration: 0.6, delay: 0.2 }}
						/>
						<motion.img
							src={mockup3}
							alt="App derecha"
							className="absolute right-1/2 bottom-0 w-48 h-auto drop-shadow-2xl translate-x-[70%] z-10"
							initial={{ opacity: 0, y: 30 }}
							animate={{ opacity: 1, y: 0 }}
							transition={{ duration: 0.6, delay: 0.4 }}
						/>
					</div>

					{/* Escritorio */}
					<div className="hidden md:flex items-end justify-center -space-x-32 lg:-space-x-40 ">
						<motion.img
							src={mockup2}
							alt="App izquierda"
							className="w-44 lg:w-64 h-auto drop-shadow-2xl -rotate-6 z-20"
							initial={{ opacity: 0, x: -100, y: 30 }}
							animate={{ opacity: 1, x: 0, y: [0, 6, 0], rotate: -6 }}
							transition={{
								opacity: { duration: 0.6, delay: 0.2 },
								x: { duration: 0.6, delay: 0.2 },
								y: { duration: 6, repeat: Infinity, ease: "easeInOut" },
							}}
						/>
						<motion.img
							src={mockup1}
							alt="App central"
							className="w-52 lg:w-72 h-auto drop-shadow-2xl z-30"
							initial={{ opacity: 0, y: 80 }}
							animate={{ opacity: 1, y: 0 }}
							transition={{ duration: 0.8, ease: "easeOut" }}
						/>
						<motion.img
							src={mockup3}
							alt="App derecha"
							className="w-44 lg:w-64 h-auto drop-shadow-2xl rotate-6 z-20"
							initial={{ opacity: 0, x: 100, y: 30 }}
							animate={{ opacity: 1, x: 0, y: [0, 6, 0], rotate: 6 }}
							transition={{
								opacity: { duration: 0.6, delay: 0.4 },
								x: { duration: 0.6, delay: 0.4 },
								y: { duration: 6, repeat: Infinity, ease: "easeInOut" },
							}}
						/>
					</div>
				</div>
			</div>

			{/* Ola inferior */}
			<div
				className="absolute bottom-0 left-0 w-full overflow-hidden leading-none"
				style={{ marginBottom: -1 }}
			>
				<svg
					className="relative block w-full h-[90px] sm:h-[100px]"
					viewBox="0 0 1440 80"
					xmlns="http://www.w3.org/2000/svg"
					preserveAspectRatio="none"
				>
					<path
						d="M0,80 C480,20 960,140 1440,20 L1440,80 L0,80 Z"
						fill="#ffffff"
					/>
				</svg>
			</div>
		</section>
	);
}
